import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';

import { ButtonWrapper } from '@dineoutltd/components';
import * as styles from './style.module.scss';
import Counter from '../FormElement/Counter';
import Tooltip from '../Tooltip';
import BookingFormFields from './BookingFormFields';
import DatePicker from '../DatePicker';
import RequestService from '../../networking/RequestService';
import { ASYNC, timeFormat } from '../../utilities/constants';
import User from '../SVGComponents/User';
import Button from '../Button';
import Place from '../../networking/resources/Place';
import ShiftNotesAndRules from '../ShiftNotesAndRules';
import Shift from '../../networking/resources/Shift';
import Calendar from '../SVGComponents/Calendar';
import Table from '../SVGComponents/Table';
import PoweredBy from '../PoweredBy';

let slotsRequest = null;
export default function BookRestaurantForm({
  restaurantId,
  restaurantInfo,
  lang,
  staticDate,
  setShowForm,
  events,
}) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [date, setDate] = useState(staticDate || null);
  const [time, setTime] = useState(null);
  const [seating, setSeating] = useState(false);
  const [suitableSalon, setSuitableSalon] = React.useState(null);
  const [salons, setSalons] = React.useState([]);
  const [slots, setSlots] = React.useState([]);
  const [onlyInside, setOnlyInside] = useState(false);
  const [onlyOutside, setOnlyOutside] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [booking, setBooking] = useState({});
  const [shifts, setShifts] = useState(
    restaurantInfo.shifts.map((shift) => Shift.plain(shift)) || [],
  );

  useEffect(() => {
    (new RequestService('client/places/salons'))
      .setParams({
        place_id: restaurantId,
      })
      .send()
      .then((response) => {
        setSalons(response.data);
      })
      .catch(() => null);
  }, [restaurantId]);

  useEffect(() => {
    if (salons.length > 0) {
      const outsideSalons = salons.filter((salon) => salon.outside);
      setOnlyInside(!outsideSalons.length);
      setOnlyOutside(salons.length === outsideSalons.length);
    }
  }, [salons]);

  useEffect(() => {
    if (salons.length > 0) {
      const suitableSalons = salons.filter((salon) => salon.outside === seating);
      setSuitableSalon(suitableSalons[0]);
    }
  }, [seating, salons]);

  useEffect(() => {
    if (onlyOutside) {
      setSeating(true);
    }
  }, [onlyOutside]);

  useEffect(() => {
    if (!suitableSalon || !date || !count) return;
    const currentDay = date.day();
    const currentDayShifts = restaurantInfo.shifts.filter((shift) => shift.day === currentDay);
    const startTime = Math.min(...currentDayShifts.map((shift) => shift.start));
    const endTime = Math.max(...currentDayShifts.map((shift) => shift.end));

    if (Math.max(...currentDayShifts.map((shift) => shift.bookable)) === 0
      || currentDayShifts.length === 0) {
      setSlots([]);
      setLoading(false);
      return;
    }

    let interval = 30;

    if (restaurantInfo.slug === 'allora-wilcza' || restaurantInfo.slug === '248159364-port-royal-fish-and-oysters') {
      interval = 15;
    }

    const slotsParams = {
      place_id: restaurantId,
      start: moment.utc(startTime * 1000).format('HH:mm'),
      end: moment.utc(endTime * 1000).format('HH:mm'),
      date: date.format('YYYY-MM-DD'),
      interval,
      people: count,
      outside: seating,
    };

    setLoading(true);
    if (slotsRequest !== null) {
      slotsRequest.source.cancel();
    }
    slotsRequest = (new RequestService('client/places/slots'))
      .setParams(slotsParams);
    slotsRequest
      .send()
      .then((response) => {
        setSlots(response.data);
        setLoading(false);
      })
      .catch(() => null);
  }, [date, count, suitableSalon]);

  useEffect(() => {
    if (!date || !time) return;
    const currentMoment = moment();
    const momentTime = moment.unix(time).utc();
    const hourFormatted = moment.unix(time).format(timeFormat);
    const activeSlots = slots.filter((slot) => !slot.disabled);

    if (activeSlots.length > 0) {
      const filteredSlots = activeSlots
        .filter((slot) => moment.unix(slot.dt).format(timeFormat) === hourFormatted);

      if (filteredSlots.length > 0) {
        setTime(filteredSlots[0].dt);
      } else if (moment(momentTime.format(timeFormat), timeFormat).isBetween(moment('06:00', timeFormat), moment(currentMoment.format(timeFormat), timeFormat))) {
        setTime(activeSlots[0].dt);
      } else {
        setTime(activeSlots[activeSlots.length - 1].dt);
      }
    }
  }, [date, slots]);

  useEffect(() => (() => {
    if (slotsRequest !== null) {
      slotsRequest.source.cancel();
    }
  }), []);

  useEffect(() => {
    (new RequestService('client/places/shifts'))
      .setParams({
        place_id: restaurantId,
      })
      .send()
      .then((response) => {
        setShifts(response.data);
      })
      .catch(() => null);
  }, []);

  useEffect(() => {
    if (!staticDate) return;
    setDate(staticDate);
    // setTime(staticDate.unix());
    setCount(2);
  }, [staticDate]);

  const clickedMinusHandler = () => {
    const newCount = count === 1 ? 1 : (count - 1);
    return setCount(newCount);
  };

  const clickedPlusHandler = () => setCount(count + 1);

  const dateHandler = (event) => setDate(event);

  const firstLetter = restaurantInfo.name[0].toLowerCase();

  const slotsWithSpecialOffers = slots.map((slot) => {
    const slotTime = moment.unix(slot.dt).utc();
    const specialOffer = events.find((offer) => {
      const offerStart = moment(offer.start);
      const offerEnd = moment(offer.end);

      const newSlotTime = moment(slotTime.format(timeFormat), timeFormat);
      const newOfferStart = moment(offerStart.format(timeFormat), timeFormat);
      const newOfferEnd = moment(offerEnd.format(timeFormat), timeFormat);

      if (newOfferEnd.isBefore(moment('06:00', timeFormat))) {
        newOfferEnd.add('1', 'day');
      }
      if (newOfferStart.isBefore(moment('06:00', timeFormat))) {
        newOfferStart.add('1', 'day');
      }

      if (newSlotTime.isBefore(moment('06:00', timeFormat))) {
        newSlotTime.add('1', 'day');
      }

      return newSlotTime.isBetween(newOfferStart, newOfferEnd, 'minutes', '[]');
    });

    return {
      disabled: slot.disabled,
      dt: slot.dt,
      id: slot.id,
      hasSpecialOffers: !!specialOffer,
    };
  });

  const beforeMidnightSlots = slotsWithSpecialOffers.filter((slot) => {
    const slotDateTime = moment.unix(slot.dt).utc().format(timeFormat);
    const morning = moment('06:00', timeFormat);
    const endOfDay = moment().endOf('day');

    return moment(slotDateTime, timeFormat).isBetween(morning, endOfDay, 'minutes', '[)');
  });

  const afterMidnightSlots = slotsWithSpecialOffers.filter((slot) => {
    const slotDateTime = moment.unix(slot.dt).utc().format(timeFormat);
    const morning = moment('06:00', timeFormat);
    const startOfDay = moment().startOf('day');

    return moment(slotDateTime, timeFormat).isBetween(startOfDay, morning, 'minutes', '[)');
  });

  return (
    <form className={styles['booking-info-block']} noValidate>
      <PoweredBy restaurantInfo={restaurantInfo} lang={lang} />

      {showSuccess ? (
        <section style={{ marginTop: 30 }}>
          <h2 className="section-title small">
            {booking.status === 'pending' ? (
              t('pageBookings:booking_requested')
            ) : (
              t('forms:booking_success')
            )}
          </h2>
          <div>
            {booking.status === 'active'
              && booking.transactions?.find((transaction) => transaction.status === 'pending')?.id
              && (
                <>
                  <p className="text-tasting"><strong>{t('pageBookings:transactions_text')}</strong></p>
                  <ButtonWrapper type="outline">
                    {t('pageBookings:pay')}
                    <a target="_blank" rel="noreferrer" href={`https://${process.env.REACT_APP_DOMAIN}/booking/${booking.id}/${booking.pnr}`}>
                      {' '}
                    </a>
                  </ButtonWrapper>
                </>
              )}
            {booking.status === 'pending' && (
              <div className="gray-block full-width" style={{ marginBottom: 20 }}>
                <h3>{t('pageBookings:not_booking_title')}</h3>
                <p>
                  {t('pageBookings:not_booking_text')}
                </p>
              </div>
            )}
            <div>
              <p className={styles['booking-info-item']}>
                <Calendar className={`icon ${styles['icon']}`} />
                {`${booking.date} ${t('pageBookings:search_from')} ${booking.time}`}
              </p>
              {moment(moment.unix(time).utc().format(timeFormat), timeFormat).isBefore(moment('06:00', timeFormat), 'minutes') && (
                <p className="text-tasting" style={{ marginTop: 0 }}>
                  {'* '}
                  {t('pageBookings:after_midnight_confirmed_note')}
                </p>
              )}

              <p className={styles['booking-info-item']}>
                <Table className={`icon ${styles['icon']}`} />
                {booking.tables && (
                  <span>{t(`pageBookings:${booking.tables[0].salon.outside ? 'outside' : 'inside'}`)}</span>
                )}
              </p>
              <p className={styles['booking-info-item']}>
                <User className={`icon ${styles['icon']}`} />
                {t('pageBookings:people_and_guest_name_text', { count: booking.people, name: booking.contactName })}
              </p>
            </div>
            {booking.status === 'pending' && (
              <div className="gray-block full-width" style={{ marginBottom: 20 }}>
                <h3>{t('pageBookings:manage_request_title')}</h3>
                <p>
                  {t('pageBookings:manage_request_text')}
                </p>
              </div>
            )}
            {booking.status !== 'pending' && (
              <Button
                text={t('book_again')}
                onClick={() => {
                  setShowSuccess(false);
                  setDate(null);
                  setTime(null);
                  setCount(0);
                  setBooking({});
                }}
                classes=""
              />
            )}
          </div>
        </section>
      ) : (
        <>
          {restaurantInfo.bookingsType === ASYNC && (
            <h2 className="section-title small">
              {t('pageBookings:request_at', {
                at: t(`${
                  firstLetter === 'в' || firstLetter === 'ф' ? 'at_long' : 'at'
                }`),
              })}
              <strong>{` ${restaurantInfo.name} `}</strong>
            </h2>
          )}
          {staticDate && (
            <button type="button" className="btn-link" onClick={() => setShowForm(false)}>
              {t('forms:select_date')}
            </button>
          )}
          <div className={styles['booking-header']}>

            <div>
              <DatePicker
                date={date}
                handleDate={(event) => dateHandler(event)}
                staticDate={staticDate}
                setShowForm={setShowForm}
              />

            </div>
            <div className={styles['guests-wrapper']}>
              <User className={styles['icon']} />
              <Counter
                count={count}
                clickedMinus={clickedMinusHandler}
                clickedPlus={clickedPlusHandler}
              />
            </div>
            <Tooltip
              title={onlyInside || onlyOutside ? t('pageBookings:seating_tooltip_inside') : t('pageBookings:seating_tooltip')}
              position="bottom"
            >
              <button
                type="button"
                className={`${styles['toggle-seating']} ${seating ? styles['active'] : ''}`}
                disabled={onlyInside || onlyOutside}
                onClick={() => setSeating(!seating)}
                data-testid="toggle-seating"
              >
                <span data-testid="seating-inside">
                  {t('pageBookings:inside')}
                </span>
                <span data-testid="seating-outside">
                  {t('pageBookings:outside')}
                </span>
              </button>
            </Tooltip>
          </div>
          {date === null || count === 0 ? (
            <h3 className={styles['please-select']}>{t('pageBookings:select_date_and_guests')}</h3>
          ) : (
            <div className={`${styles['pick-hour']} ${loading ? styles['loading'] : ''}`}>
              {slots.length < 1 && (
                <h3 className="section-sub-title"><strong>{t('pageBookings:no_time_slots')}</strong></h3>
              )}
              <div className={styles['hours-wrapper']}>
                {beforeMidnightSlots.map((slot) => (
                  <button
                    type="button"
                    key={slot.dt}
                    className={`${(slot.dt === time && !slot.disabled) ? styles['selected-hour'] : ''} ${slot.hasSpecialOffers ? styles['has-special-offers'] : ''}`}
                    onClick={() => {
                      setTime(slot.dt);
                    }}
                    disabled={slot.disabled}
                  >
                    <span className={styles['hour']}>{moment.unix(slot.dt).utc().format(timeFormat)}</span>
                  </button>
                ))}
              </div>
              {afterMidnightSlots.length > 0 && (
                <div className={styles['after-midnight-wrapper']}>
                  <h2>{t('pageBookings:after_midnight_title')}</h2>
                  <p style={{ marginTop: 0 }} className="text-tasting">
                    {'* '}
                    {t('pageBookings:after_midnight_note')}
                  </p>
                  <div className={styles['hours-wrapper']}>
                    {afterMidnightSlots.map((slot) => (
                      <button
                        type="button"
                        key={slot.dt}
                        className={`${(slot.dt === time && !slot.disabled) ? styles['selected-hour'] : ''} ${slot.hasSpecialOffers ? styles['has-special-offers'] : ''}`}
                        onClick={() => {
                          setTime(slot.dt);
                        }}
                        disabled={slot.disabled}
                      >
                        <span
                          className={styles.hour}
                        >
                          {moment.unix(slot.dt).utc().format(timeFormat)}
                        </span>
                        <sup className={styles['after-midnight']}>
                          {'* '}
                          {moment.unix(slot.dt).utc().format('DD.MM')}
                        </sup>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
          <div style={{ marginTop: 30, marginBottom: 30 }}>
            <ShiftNotesAndRules
              shifts={shifts}
              date={date}
              time={time}
              count={count}
              restaurantInfo={restaurantInfo}
            />
          </div>
          <BookingFormFields
            time={time}
            guests={count}
            setShowSuccess={setShowSuccess}
            setBooking={setBooking}
            outside={seating}
            restaurantId={restaurantId}
            restaurantInfo={restaurantInfo}
            lang={restaurantInfo.city.country.code.toLowerCase()}
          />
        </>
      )}
    </form>
  );
}

BookRestaurantForm.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  restaurantInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Place)]).isRequired,
  events: PropTypes.oneOfType([PropTypes.array]),
  lang: PropTypes.string,
  staticDate: PropTypes.instanceOf(moment),
  setShowForm: PropTypes.func,
};

BookRestaurantForm.defaultProps = {
  lang: null,
  staticDate: null,
  setShowForm: () => {},
  events: [],
};
